<template>
  <template v-if="item.isEdit">
    <!-- 编辑状态的显示逻辑 -->
    <el-menu-item :index="item.id">
      <span class="num-id-prefix">{{ item.numId }}</span>
      <el-input
        v-model="inputValue"
        @blur="handleSave"
        @keyup.enter="handleSave"
        @keyup.esc="handleCancel"
        ref="inputRef"
        :loading="isLoading"
        :disabled="isLoading"
        :data-numid="item.numId"
        @keydown="handleKeyDown"
      >
        <template #suffix>
          <el-icon v-if="isLoading" class="is-loading">
            <Loading />
          </el-icon>
        </template>
      </el-input>
    </el-menu-item>
  </template>
  <template v-else>
    <!-- 非编辑状态的显示逻辑 -->
    <el-sub-menu
      v-if="item.children?.length"
      :index="item.id"
      :key="`${item.id}-${refreshKey}`"
      @contextmenu.prevent="(e) => handleContextMenu(e, item)"
    >
      <template #title>
        <span @click.stop="handleClick(item)">
          {{ getDisplayName(item) }}
        </span>
      </template>
      <!-- 递归渲染子项 -->
      <menu-item-component
        v-for="child in sortedChildren"
        :key="child.id"
        :item="child"
        :input-name="inputName"
        @update:item="$emit('update:item', $event)"
        @update:inputName="$emit('update:inputName', $event)"
        @save="$emit('save')"
        @cancel="$emit('cancel')"
        @context-menu="
          (event, menuItem) => $emit('context-menu', event, menuItem)
        "
        @remove="$emit('remove', $event)"
        @select="$emit('select', $event)"
      />
    </el-sub-menu>
    <el-menu-item
      v-else
      :index="item.id"
      @click="handleClick(item)"
      @contextmenu.prevent="(e) => handleContextMenu(e, item)"
    >
      <span>{{ getDisplayName(item) }}</span>
    </el-menu-item>
  </template>
</template>

<script setup lang="ts">
import { ref, watch, computed, nextTick } from "vue";
import { type MenuItem } from "@/types/menu";
import {
  getStandardById,
  getTermsByStandardId,
  getLevelDetail,
} from "@/api/menu";
import {
  addLevel,
  updateLevel,
  insertLevel,
  searchLevelDetail,
} from "@/api/level";
import { ElMessage } from "element-plus";
import { Check, Close, Loading } from "@element-plus/icons-vue";

// 修改 props 定义
const props = defineProps<{
  item: MenuItem;
  inputName: string;
  "onUpdate:item": (value: MenuItem) => void;
}>();

// 修改 emit 定义
const emit = defineEmits<{
  "update:inputName": [value: string];
  "update:item": [value: MenuItem];
  save: [];
  cancel: [];
  "context-menu": [event: MouseEvent, item: MenuItem];
  remove: [id: string];
  select: [item: MenuItem];
  "refresh-data": [data: { id: string; newName: string }]; // 新增
}>();

const inputRef = ref<HTMLInputElement | null>(null);
const inputValue = ref(props.inputName || props.item.name);

watch(
  () => props.inputName,
  (newVal) => {
    inputValue.value = newVal;
  }
);

// 添加接口调用状态控制
const lastCallTime = ref(0);
const minInterval = 100000; // 最小调用间隔（毫秒）
const isLoading = ref(false);

// 添加接口调用检查函数
const checkApiCall = () => {
  const now = Date.now();
  if (now - lastCallTime.value < minInterval) {
    // ElMessage.warning('请勿频繁操作')
    return false;
  }
  if (isLoading.value) {
    ElMessage.warning("请等待当前操作完成");
    return false;
  }
  lastCallTime.value = now;
  return true;
};

const handleClick = async (item: MenuItem) => {
  try {
    if (item.type === "terms") {
      // 调用获取术语列表接口
      const response = await getTermsByStandardId(Number(item.standardId));

      // 增加更严格的响应数据检查
      if (
        (response?.code === 1 && response?.data) ||
        Array.isArray(response.data)
      ) {
        const terms = response.data.map((term) => ({
          id: term.id || "",
          standardId: item.standardId || "",
          standardName: item.standardName || "",
          cName: term.cName || "",
          eName: term.eName || "",
          explanation: term.explanation || "",
          image: term.image || "",
        }));

        emit("select", {
          ...item,
          type: "terms",
          terms,
        });
      } else {
        console.warn("获取术语列表返回数据格式不正确:", response);
        ElMessage.warning("获取术语数据格式不正确");
      }
    } else if (item.type === "standard") {
      const response = await getStandardById(item.id);

      // 修改数据校验逻辑
      if (response?.code === 1 && response?.data?.standard) {
        const standardData = {
          ...item,
          ...response.data.standard,
          file: response.data.file || null,
          fileName: response.data.fileName || null,
          type: "standard",
          // 如果 terms 存在则使用，否则设为空数组
          terms:
            response.data.terms?.map((term) => ({
              id: term.id || "",
              standardId: term.standardId || "",
              standardName: item.standardName || "",
              cName: term.cName || "",
              eName: term.eName || "",
              explanation: term.explanation || "",
              image: term.image || "",
            })) || [],
        };
        emit("select", standardData);
      } else {
        console.warn("获取标准详情失败:", response);
        ElMessage.warning("获取标准详情失败");
      }
    } else if (item.type === "level") {
      if (!item.id || !item.numId) {
        ElMessage.error("指标信息不完整");
        return;
      }

      try {
        const currentLevel = Number(item.level);

        if (isNaN(currentLevel) || currentLevel < 1 || currentLevel > 4) {
          ElMessage.error("指标级别不正确");
          return;
        }
        const response = await getLevelDetail(item.id, currentLevel);

        if (response?.code === 1 && response?.data) {
          const levelData = {
            ...item,
            ...response.data,
            id: String(response.data.id),
            standardId: String(response.data.standardId || item.standardId),
            numId: item.numId,
            level: currentLevel,
            type: "level",
            content: response.data.content || "",
            image: response.data.image || "",
            ifNextLevel: response.data.ifNextLevel || 0,
            children: item.children || [],
          };
          emit("select", levelData);
        } else {
          console.error("获取指标详情失败，请您先保存:", response);
          ElMessage.warning(response?.msg || "获取数据失败，请您先保存");
        }
      } catch (error) {
        console.error("请求失败:", error);
        ElMessage.error(error?.message || "获取指标详情失败");
      }
    }
  } catch (error) {
    console.error("请求失败:", error);
    ElMessage.error(error?.message || "获取指标详情失败");
  }
  // emit("refresh-data"); // 新增刷新数据
};

// 添加检查层级的函数
const isMaxLevel = (item: MenuItem): boolean => {
  return item.type === "level" && item.level >= 4;
};

// 修改右键菜单的显示逻辑
const handleContextMenu = (event: MouseEvent, item: MenuItem) => {
  event.preventDefault();
  event.stopPropagation();

  emit("context-menu", event, {
    ...item,
    type: item.type, // 确保类型信息被传递
    isMaxLevel: isMaxLevel(item),
    level: item.level,
  });
};

// 监听编辑状态变化，自动聚焦输入框
watch(
  () => props.item.isEdit,
  (newVal) => {
    if (newVal) {
      nextTick(() => {
        inputRef.value?.focus();
      });
    }
  }
);

// 修改保存处理逻辑
const handleSave = async () => {
  if (!inputValue.value.trim()) {
    ElMessage.warning("指标名不能为空");
    return;
  }
  if (!checkApiCall()) return;

  if (!props.item.numId) {
    ElMessage.warning("指标编号不能为空");
    return;
  }
  try {
    isLoading.value = true;

    const basePayload = {
      standardId: Number(props.item.standardId),
      level: Number(props.item.level) || 1,
      numId: props.item.numId,
      name: inputValue.value.trim(),
      content: props.item.content || "",
      ifNextLevel: props.item.ifNextLevel || 0,
      imageList: props.item.imageList || [],
    };

    let response;
    let finalId: string | null = null;
    // 处理插入逻辑
    if (props.item.insertBefore) {
      const insertPayload = {
        ...basePayload,
        level: props.item.level,
        // position: props.item.insertPosition, // 确保传递插入位置
        relativeNumId: props.item.numId,
      };
      // 第一步：执行插入操作
      response = await insertLevel(insertPayload);
      // 第二步：构造查询参数（修正类型问题）
      const searchParams = {
        level: basePayload.level.toString(),
        numId: props.item.numId,
        // position: props.item.insertPosition, // 确保传递插入位置
        standardId: basePayload.standardId.toString(),
      };
      // 第三步：调用查询接口获取新ID
      const searchResponse = await searchLevelDetail(searchParams);
      if (searchResponse?.code === 1 && searchResponse.data?.id) {
        finalId = searchResponse.data.id.toString();
      } else {
        console.error("查询新增记录失败", searchResponse);
        ElMessage.error("获取新增记录ID失败");
        return;
      }
    }
    // 处理新增逻辑
    else if (props.item.isNew) {
      response = await addLevel(basePayload);
    }
    // 处理更新逻辑
    else {
      response = await updateLevel({
        ...basePayload,
        id: Number(props.item.id),
      });
    }

    if (response?.code === 1) {
      if (finalId == null) {
        // 统一从响应中获取 ID
        finalId = response.data?.id;
      }
      // 统一从响应中获取 ID
      // 当新增或插入操作时必须获取新 ID
      if ((props.item.isNew || props.item.insertBefore) && !finalId) {
        console.error("保存失败：接口未返回有效ID", response);
        ElMessage.error("保存失败：服务器未返回有效ID");
        return;
      }

      const updatedItem = {
        ...props.item,
        id: finalId || props.item.id, // 更新操作沿用原 ID
        name: inputValue.value,
        isEdit: false,
        isNew: false,
        insertBefore: false, // 清除插入状态
      };

      await nextTick();

      // 触发保存事件
      emit("save");
      // 新增/插入时更新选中状态
      if (props.item.isNew || props.item.insertBefore) {
        emit("select", {
          ...updatedItem,
          numId: updatedItem.numId,
        });
      }

      ElMessage.success(
        props.item.insertBefore
          ? "插入成功"
          : props.item.isNew
          ? "新增成功"
          : "更新成功"
      );
    } else {
      ElMessage.error(response?.msg || "保存失败");
    }
  } catch (error) {
    console.error("保存失败:", error);
    ElMessage.error("保存失败");
  } finally {
    isLoading.value = false;
  }
};
const handleCancel = () => {
  inputValue.value = props.item.name;
  emit("update:item", { ...props.item, isEdit: false });
};

// 添加显示名称的处理函数
const getDisplayName = (item: MenuItem) => {
  if (item.type === "standard") {
    return item.standardName || item.name;
  } else if (item.type === "level") {
    return `${item.numId || ""} ${item.name}`;
  } else if (item.type === "terms") {
    return "术语";
  }
  return item.name;
};

const refreshKey = ref(0);
// 监听数据变化
watch(
  () => props.item,
  () => {
    refreshKey.value++;
  },
  { deep: true }
);

const compareNumIds = (a: string, b: string) => {
  const aParts = a.split(".").map(Number);
  const bParts = b.split(".").map(Number);
  for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
    const aVal = aParts[i] || 0;
    const bVal = bParts[i] || 0;
    if (aVal !== bVal) return aVal - bVal;
  }
  return 0;
};

const sortedChildren = computed(() => {
  return (
    props.item.children
      ?.slice()
      .sort((a, b) => compareNumIds(a.numId, b.numId)) || []
  );
});

const handleKeyDown = (event) => {
  if (event.key === "Enter") {
    // 触发刷新
    refreshData();
  }
};

const refreshData = async () => {
  await emit("menu-action", { type: "refresh" });
};
</script>
<style scoped>
.action-buttons .el-button {
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
}

.num-id-prefix {
  margin-right: 8px;
  color: #909399;
  user-select: none;
}

/* 添加加载状态样式 */
.is-loading {
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
